import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";

const LINE_OA_MANAGER_URL = "https://manager.line.biz/";

const styles = {
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  steps: {
    margin: 0,
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  subSteps: {
    margin: 0,
    paddingLeft: "20px",
    listStyleType: "upper-alpha",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface LineWebhookCheckModalProps {
  open: boolean;
  /** Basic ID of the OA, used to deep-link into LINE Official Account Manager. */
  basicId?: string | undefined;
  onClose: () => void;
  onRecheck: () => void;
  rechecking: boolean;
  /**
   * Outcome of the last recheck: the webhook is still off, or its status
   * could not be read. `null` before any recheck or while one runs.
   */
  recheckResult: "stillOff" | "unavailable" | null;
}

/**
 * Asks the customer to turn on "Use webhook" for a LINE OA that was just
 * connected; without it, no customer message reaches CAAC. Copy and steps
 * follow MAAC's `WebhookCheckModal`.
 */
export const LineWebhookCheckModal: React.FC<LineWebhookCheckModalProps> =
  function LineWebhookCheckModal({
    open,
    basicId,
    onClose,
    onRecheck,
    rechecking,
    recheckResult,
  }) {
    const { t } = useTranslation();
    // The basic ID comes from LINE through the backend; encode it so it can
    // only fill the path segment it belongs to.
    const accountManagerUrl = basicId
      ? `${LINE_OA_MANAGER_URL}account/@${encodeURIComponent(basicId.replace(/^@/, ""))}`
      : LINE_OA_MANAGER_URL;

    return (
      <Modal
        open={open}
        handleClose={onClose}
        width={676}
        title={t(
          "channels.line.webhookCheck.title",
          "Turn on webhook for your LINE Official Account",
        )}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button
              variant="secondary"
              onClick={onRecheck}
              loading={rechecking}
              disabled={rechecking}
            >
              {t("channels.line.webhookCheck.recheck", "Check again")}
            </Button>
            <Button variant="primary" onClick={onClose}>
              {t("channels.line.webhookCheck.close", "Got it")}
            </Button>
          </Stack>
        }
      >
        <Box sx={styles.content} data-test="line-webhook-check">
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t(
              "channels.line.webhookCheck.desc1",
              "Webhook is off for this LINE Official Account, so customer messages won't reach CAAC.",
            )}
          </Typography>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t(
              "channels.line.webhookCheck.desc2",
              "Follow the steps below, then select Check again.",
            )}
          </Typography>
          <ExternalLink
            href={t(
              "channels.line.webhookCheck.tutorialUrl",
              "https://crescendolab.zendesk.com/hc/zh-tw",
            )}
            trailingIcon
          >
            {t("channels.line.webhookCheck.tutorial", "View full guide")}
          </ExternalLink>
          <Box>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgTitle}
            >
              {t("channels.line.webhookCheck.steps", "Steps")}
            </Typography>
            <Box component="ol" sx={styles.steps}>
              <Box component="li">
                <Typography variant="body" color={theme.colors.staticFgBody}>
                  {t(
                    "channels.line.webhookCheck.step1",
                    "Go to LINE Official Account Manager.",
                  )}{" "}
                  <ExternalLink href={accountManagerUrl} trailingIcon>
                    {t(
                      "channels.line.webhookCheck.step1.link",
                      "LINE Official Account Manager",
                    )}
                  </ExternalLink>
                </Typography>
              </Box>
              <Box component="li">
                <Typography variant="body" color={theme.colors.staticFgBody}>
                  {t(
                    "channels.line.webhookCheck.step2",
                    "Select Settings in the top-right corner.",
                  )}
                </Typography>
              </Box>
              <Box component="li">
                <Typography variant="body" color={theme.colors.staticFgBody}>
                  {t(
                    "channels.line.webhookCheck.step3",
                    "Select Response settings in the left menu.",
                  )}
                </Typography>
                <Box component="ol" sx={styles.subSteps}>
                  <Box component="li">
                    <Typography
                      variant="body"
                      color={theme.colors.staticFgBody}
                    >
                      {t(
                        "channels.line.webhookCheck.webhookOn",
                        "Turn on Webhook.",
                      )}
                    </Typography>
                    <Typography
                      variant="body"
                      color={theme.colors.staticFgNote}
                    >
                      {t(
                        "channels.line.webhookCheck.suggestion",
                        "We also recommend turning off auto-reply messages so customers don't get duplicate replies.",
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          {recheckResult === "stillOff" ? (
            <Typography
              variant="body"
              color={theme.colors.staticFgNegative}
              role="alert"
            >
              {t(
                "channels.line.webhookCheck.stillOff",
                "Webhook still isn't on. Make sure your settings are saved, then check again.",
              )}
            </Typography>
          ) : null}
          {recheckResult === "unavailable" ? (
            <Typography
              variant="body"
              color={theme.colors.staticFgNote}
              role="status"
            >
              {t(
                "channels.line.webhookCheck.unavailable",
                "Couldn't check the webhook status right now. Please try again later.",
              )}
            </Typography>
          ) : null}
        </Box>
      </Modal>
    );
  };
